import { ErrorResponse, User } from 'oidc-client-ts';
import { logTrace } from '../telemetry/logTrace';
import { benefitUserManager } from './oidcConfig';

export type SilentRenewResult =
  | { user: User; rejected: false; error?: undefined }
  | { user: null; rejected: boolean; error: string };

// Unlike useAuth().signinSilent, which swallows the error and returns null,
// this tells apart a refresh token rejected by the IDP (an OAuth error
// response such as invalid_grant: expired or revoked, retrying is pointless)
// from a transient failure (network error, timeout) that is worth retrying.
// The react-oidc-context state is still updated through the userLoaded event.
export const renewTokenSilently = async (): Promise<SilentRenewResult> => {
  try {
    const user = await benefitUserManager.signinSilent();
    if (user) {
      return { user, rejected: false };
    }
    return { user: null, rejected: false, error: 'no user returned' };
  } catch (e) {
    if (e instanceof ErrorResponse) {
      return { user: null, rejected: true, error: e.error ?? e.message };
    }
    return {
      user: null,
      rejected: false,
      error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
    };
  }
};

const RENEW_RETRY_DELAYS_MS = [2000, 5000, 10000];

const delay = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

const isPageUsable = () =>
  document.visibilityState === 'visible' && navigator.onLine;

// A renew attempt made while the page is hidden (e.g. the phone is going to
// sleep and its network is being cut) fails with "Failed to fetch" even though
// navigator.onLine is still true, so attempts are only made on a visible page.
const waitUntilPageUsable = async () => {
  while (!isPageUsable()) {
    await new Promise<void>(resolve => {
      const controller = new AbortController();
      const onChange = () => {
        controller.abort();
        resolve();
      };
      document.addEventListener('visibilitychange', onChange, controller);
      globalThis.addEventListener('online', onChange, controller);
    });
  }
};

// Right after a device wakes up (or a page is reloaded/restored) the network is
// often not ready yet, so the /connect/token request fails in the browser
// without reaching the IDP. Wait for connectivity and retry a few times before
// giving up; a rejection by the IDP is returned right away.
export const renewTokenWithRetry = async (
  source: string,
): Promise<SilentRenewResult> => {
  let result: SilentRenewResult = {
    user: null,
    rejected: false,
    error: 'not attempted',
  };

  for (let attempt = 0; attempt <= RENEW_RETRY_DELAYS_MS.length; attempt++) {
    await waitUntilPageUsable();
    result = await renewTokenSilently();
    if (result.user) {
      return result;
    }

    const pageUsable = isPageUsable();
    logTrace(`${source}: silent renew failed`, {
      attempt,
      counted: pageUsable,
      rejected: result.rejected,
      error: result.error,
    });

    if (result.rejected) {
      return result;
    }

    // the page went hidden/offline while the request was in flight, so the
    // failure says nothing about the refresh token; retry once it is back
    if (!pageUsable) {
      attempt--;
      continue;
    }

    if (attempt < RENEW_RETRY_DELAYS_MS.length) {
      await delay(RENEW_RETRY_DELAYS_MS[attempt]);
    }
  }
  return result;
};
